import type { AcpPlanData } from '@crowdex/agent-contracts';
import { cn } from '@crowdex/ui/lib/utils';
import { ChevronRight, ListChecks } from 'lucide-react';
import { planProgressIndex } from '@/lib/plan-progress';
import { TRANSCRIPT_CARD } from './command-card';

/**
 * The agent's plan as a card (design D10): "Plan · 3 / 5", one segment per step (done green,
 * current blue) and the step it is on. Its one action opens the Plan tab of the panel.
 */
export function DesktopPlanCard({ data, onOpen }: { data: AcpPlanData; onOpen?: () => void }) {
  const entries = 'entries' in data ? data.entries : [];
  const done = entries.filter((entry) => entry.status === 'completed').length;
  const current = entries[planProgressIndex(entries)];
  const allDone = entries.length > 0 && done === entries.length;
  const Root = onOpen === undefined ? 'div' : 'button';
  return (
    <Root
      className={cn(
        TRANSCRIPT_CARD,
        'block w-full px-3.5 py-3 text-left',
        onOpen !== undefined && 'transition-colors hover:border-foreground/20',
      )}
      data-plan-card
      {...(onOpen === undefined ? {} : { onClick: onOpen, type: 'button' as const })}
    >
      <span className="flex items-center gap-2.5">
        <ListChecks aria-hidden className="size-4 shrink-0 text-muted-foreground" />
        <span className="flex-1 font-semibold text-foreground text-sm">Plan</span>
        {entries.length > 0 && (
          <span className="type-meta text-muted-foreground tabular-nums">
            {done} / {entries.length}
          </span>
        )}
        {onOpen !== undefined && (
          <ChevronRight aria-hidden className="size-4 shrink-0 text-muted-foreground" />
        )}
      </span>
      {entries.length > 0 && (
        <span aria-hidden className="mt-2.5 flex gap-1">
          {entries.map((entry, index) => (
            <span
              className={cn(
                'h-1 flex-1 rounded-full',
                entry.status === 'completed'
                  ? 'bg-success'
                  : entry.status === 'in_progress'
                    ? 'bg-running'
                    : 'bg-control',
              )}
              // biome-ignore lint/suspicious/noArrayIndexKey: Plan entries have no id; their order is their identity.
              key={index}
            />
          ))}
        </span>
      )}
      {current !== undefined && (
        <span className="type-meta mt-2.5 block truncate text-foreground/85">
          {allDone ? 'Done' : `Now: ${current.content}`}
        </span>
      )}
    </Root>
  );
}
